import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { DirectionsRenderer, GoogleMap, InfoWindow, Marker, useGoogleMap, useJsApiLoader, useLoadScript } from '@react-google-maps/api';
import IGenericResult from '../../interfaces/IGenericResult';
import { getCurrentPosition } from '../../lib/util';

export interface IMarker extends IGenericResult {
  lat: number;
  lng: number;
  label: string;
}

export interface ILatLng {
  lat: number;
  lng: number;
}

const libraries: any = ['places', 'geometry'];

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: 32.379223,
  lng: -86.307739
};

/*
  https://www.npmjs.com/package/@react-google-maps/api
*/

const Map = (props: any) => {
  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
    libraries
  });
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<IMarker[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number }>();
  const [directions, setDirections] = useState<google.maps.DirectionsResult | undefined>(undefined);

  useEffect(() => {
    setMarkers(buildMarkers(props.currentChunk || []));
  }, [props.currentChunk]);
  useEffect(() => {
    if (props.profileDirections) {
      setDirections(props.profileDirections);
    }
  }, [props.profileDirections]);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await getCurrentPosition();
        const { latitude, longitude } = res.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
      } catch (err) {
        console.error('ERROR', err);
      }
    })();
  }, []);

  useEffect(() => {
    fitMarkerBounds(googleMap, markers, !!props.profileDirections);
  }, [googleMap, markers, props.profileDirections]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setGoogleMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setGoogleMap(null);
  }, []);

  return isLoaded ? (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={10} onLoad={onLoad} onUnmount={onUnmount}>
      {markers?.map((marker, index) => (
        <Marker label={marker.label} key={index} position={{ lat: marker.lat, lng: marker.lng }} onClick={() => setSelectedMarker(marker)} />
      ))}
      <DirectionsRenderer directions={directions} />
      {selectedMarker ? (
        <InfoWindow position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }} onCloseClick={() => setSelectedMarker(null)}>
          <div>
            <h3>{selectedMarker.name}</h3>
            <p>{selectedMarker.phone}</p>
            <p>
              {selectedMarker.addressLine1}, {selectedMarker.addressLine2 ? selectedMarker.addressLine2 + ', ' : ''}
              {selectedMarker.city}, {selectedMarker.state} {selectedMarker.zipCode}
            </p>
            <button onClick={() => getDirections(currentLocation, { lat: selectedMarker.lat, lng: selectedMarker.lng }, setDirections)}>Get Directions</button>
          </div>
        </InfoWindow>
      ) : null}
    </GoogleMap>
  ) : (
    <></>
  );
};

//buildMarkers
const buildMarkers = (chunk: IGenericResult[]): IMarker[] => {
  const markers: IMarker[] = [];

  for (let i = 0; i < chunk.length; i++) {
    const currentChunk = chunk[i];
    if (!currentChunk) continue;

    const lat = Number(currentChunk.latitude);
    const lng = Number(currentChunk.longitude);

    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      markers.push({
        recordId: currentChunk.recordId,
        lat,
        lng,
        label: `${i + 1}`,
        name: currentChunk.name,
        locationName: currentChunk.locationName,
        specialty: currentChunk.specialty,
        specialtyGroup: currentChunk.specialtyGroup,
        specialties: currentChunk.specialties,
        phone: currentChunk.phone,
        fax: currentChunk.fax,
        addressLine1: currentChunk.addressLine1,
        addressLine2: currentChunk.addressLine2,
        zipCode: currentChunk.zipCode,
        city: currentChunk.city,
        state: currentChunk.state,
        providerNPI: currentChunk.providerNPI,
        uniqueProviderId: '',
        firstName: '',
        lastName: '',
        credentials: '',
        practices: null,
        language: null,
        hoursOfOperation: undefined,
        approximateDistance: undefined,
        companyId: null,
        boardCertification: undefined,
        directorySection: null,
        latitude: lat,
        longitude: lng
      });
    }
  }

  
  const stringMarkers = markers.map((marker) => marker.lat + ',' + marker.lng);
  sessionStorage.setItem('markers', JSON.stringify(stringMarkers));

  return markers;
};

const fitMarkerBounds = (map: google.maps.Map | null, markers: IMarker[], hasProfileDirections: boolean) => {
  if (!window.google || hasProfileDirections || !map || !markers.length) return;

  const bounds = new window.google.maps.LatLngBounds();
  markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
  map.fitBounds(bounds, 60);

  const zoom = map.getZoom();
  if (zoom && zoom > 10) {
    map.setZoom(10);
  }
};

const getDirections = (origin: any, destination: any, setDirections: any) => {
  if (!origin?.lat || !origin?.lng) return;
  const DirectionsService = new google.maps.DirectionsService();

  DirectionsService.route(
    {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      travelMode: google.maps.TravelMode.DRIVING
    },
    (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error(`error fetching directions ${result}`);
      }
    }
  );
};

export default Map;
